import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { semanticCapabilities } from '../data/semanticKnowledge';
import { fadeUp } from '../utils/motion';
import { PageHero } from '../components/layout/PageHero';
import { SkpLlmTeaser } from '../components/semantic/SkpLlmTeaser';
import { SkpWorkflow } from '../components/semantic/SkpWorkflow';
import { SkpResearch } from '../components/semantic/SkpResearch';
import { SkpCapabilityFoundation } from '../components/semantic/SkpCapabilityFoundation';
import { SkpControls } from '../components/semantic/SkpControls';
import { SkpFoundation } from '../components/semantic/SkpFoundation';
import { SkpCTA } from '../components/semantic/SkpCTA';
export function SemanticKnowledgePage() {
  return <>
      <PageHero eyebrow="Products" title="Semantic Knowledge Platform" lead="The knowledge infrastructure that connects and governs concepts, relationships, sources, and approved knowledge, with knowledge graphs, semantic search, and AI-assisted research." body={['It turns scattered research and content into connected, usable knowledge — defining concepts and relationships, linking evidence across sources, and pairing AI-assisted discovery with expert review.']} data-id="element-2116" data-mp="qyeffx6oqyej">
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8" data-id="element-2117" data-mp="k1xuaqe2klay">
          <a href="/contact" className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-2118" data-mp="1howgu8b1scv4">
            Contact Us
            <ArrowRight size={18} aria-hidden="true" data-id="element-2119" data-mp="12zqjkpvrswpv" />
          </a>
          <a href="/semantic-knowledge#how-it-works" onClick={event => {
          event.preventDefault();
          document.getElementById('how-it-works')?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }} className="inline-flex items-center gap-2 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-2120" data-mp="12zgur5mgovlr">
            See how it works
          </a>
        </div>
      </PageHero>

      <section className="bg-surface-bg py-20 lg:py-28" data-id="element-2121" data-mp="3g66o97g58fsg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-2122" data-mp="3l4rq1bb1n7j0">
          <motion.h2 {...fadeUp} className="lg:col-span-4 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight leading-tight" data-id="element-2123" data-mp="374p9ptidwm5v">
            What the platform brings together
          </motion.h2>
          <motion.dl {...fadeUp} className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 border-t border-line" data-id="element-2124" data-mp="2x9xbcngz9va1">
            {semanticCapabilities.map(item => <div key={item.title} className="border-b border-line py-7" data-id="element-2125" data-mp="10fasr3herf7u">
                <dt className="font-heading font-bold text-xl text-ink-dark mb-2" data-id="element-2126" data-mp="10d800bovf7zf">{item.title}</dt>
                <dd className="text-ink-muted leading-relaxed" data-id="element-2127" data-mp="2kv8vincsn2yl">{item.body}</dd>
              </div>)}
          </motion.dl>
        </div>
      </section>

      <SkpLlmTeaser data-id="element-2128" data-mp="2uo7qqo2sprr6" />
      <SkpWorkflow data-id="element-2129" data-mp="2uoh8jodossls" />
      <SkpResearch data-id="element-2130" data-mp="2uozg3h5sut90" />

      <section aria-labelledby="skp-noetivra" className="bg-surface-bg border-y border-line py-20 lg:py-28" data-id="element-2131" data-mp="2pwprq5kc218u">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-2132" data-mp="33nbazvdp8d79">
          <div className="lg:col-span-5" data-id="element-2133" data-mp="2yr44o1he4zos">
            <p className="text-sm font-medium text-teal mb-3" data-id="element-2134" data-mp="3dmae6yfp3gmv">Noetivra · Coming soon</p>
            <h2 id="skp-noetivra" className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight" data-id="element-2135" data-mp="38meal0awtr7u">
              Explore connected knowledge through Noetivra
            </h2>
          </div>
          <div className="lg:col-span-7" data-id="element-2136" data-mp="1eb97py7nditk">
            <p className="text-xl text-ink-dark leading-relaxed mb-6" data-id="element-2137" data-mp="5cr8wnlnk212">
              Noetivra would provide interactive views of knowledge maintained in
              SKP. Users could navigate relationships, inspect supporting
              passages, compare findings, annotate questions, and participate in
              authorized review workflows.
            </p>
            <a href="/noetivra#research" className="inline-flex items-center gap-2 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-2138" data-mp="3iquovp8qr09j">
              Explore Noetivra
              <ArrowRight size={16} aria-hidden="true" data-id="element-2139" data-mp="3rqr7pa8g94wo" />
            </a>
          </div>
        </motion.div>
      </section>

      <SkpCapabilityFoundation data-id="element-2140" data-mp="ezi5wwqulyg" />
      <SkpControls data-id="element-2141" data-mp="300mlr751k9tc" />
      <SkpFoundation data-id="element-2142" data-mp="301642oylt07w" />
      <SkpCTA data-id="element-2143" data-mp="2zzv3sz4daalj" />
    </>;
}